import { api, USE_PROD } from "./api";
import { setGeo, userStore } from "../redux/user";
import { v4 } from "uuid";
import { getConfiguredHost, getServiceHost } from "./runtimeHost";

import {
  doStartReconnectTimer,
  endReconnectTimer,
  setConnectionBlockedReason,
  setOtherLoginShow,
  setOtherLoginSameBrowser,
  setWsConnected,
  updateReconnectTimer,
  wsConnectionStore,
  setWebsocketUrl,
} from "../redux/wsConnection";

import { routeIncommingMessage } from "./wsInRoutes";
import { LoginWs } from "./wsOutRoutes";
import { wsDebug } from "./wsDebug";
import {
  redirectRussiaToFallback,
  reportRussiaSiteSettings,
  reportRussiaSiteSettingsFailed,
  reportRussiaWebsocketEvent,
} from "./clientHealth";
import { isTerminalWebsocketRejection } from "./websocketRejection";
import { isReadingRoute } from "./websocketRoute";

let offlineDetectionInitialized = false;
let offlineTimeOut;

export let websocket = null;
let pingInterval: any;
let postTimeout: any;
export let longPollingUrl = null;
let useLongPolling = false;
let longPollingSwitchStarted = false;

const PING_INTERVAL_MS = 5000;
const CLIENT_RECONNECT_CLOSE_CODE = 4000;
const SITE_SETTINGS_TIMEOUT_MS = 10000;
const SITE_SETTINGS_RETRY_MS = 1000;
const SITE_SETTINGS_BACKGROUND_RETRY_MS = 60000;
const WEBSOCKET_DISCOVERY_TIMEOUT_MS = 6000;
const LAST_KNOWN_WS_STORAGE_KEY = "chesshotel:last-known-ws";
const WEBSOCKET_DISCOVERY_WS_NUMBERS = [1, 2, 3];
const WEBSOCKET_DISCOVERY_TEST_PORTS: Record<number, string> = {
  1: "9090",
  2: "9091",
  3: "9092",
};
const AUTO_LONG_POLLING_FALLBACK_ENABLED =
  process.env.NEXT_PUBLIC_RU_AUTO_LONG_POLLING !== "false";
const AUTO_LONG_POLLING_CLOSE_WINDOW_MS = 10 * 60 * 1000;
const AUTO_LONG_POLLING_MIN_1006_CLOSES = 2;
const AUTO_LONG_POLLING_GAME_PATH_RE = /^\/(?:ru\/)?game(?:\/|$)/;

let lastPingAt = 0;
let lastPongAt = 0;
let lastMessageAt = 0;
let websocketOpenedAt = 0;
let websocketUserKey = "";
let watchdogLiveGameActive = false;
let autoLongPollingFallbackStarted = false;
let liveGame1006CloseGameId = "";
let liveGame1006CloseCount = 0;
let firstLiveGame1006CloseAt = 0;
let websocketDiscoveryPromise: Promise<number | null> | null = null;
let terminalWebsocketRejection: string | null = null;
const tabId = v4();
let tabConnectionChannel: BroadcastChannel | null = null;
let tabFocusListenersInitialized = false;

function handleTabFocus() {
  if (
    document.visibilityState === "hidden" ||
    isReadingRoute(window.location.pathname) ||
    !userStore.getState().username
  ) {
    return;
  }

  const { otherLoginShow, otherLoginSameBrowser } = wsConnectionStore.getState();
  if (otherLoginShow) {
    if (otherLoginSameBrowser) {
      useConnectionInThisTab();
    }
  } else if (!hasActiveWebsocketConnection()) {
    initWebsocketConnect();
  }
}

function ensureTabConnectionChannel() {
  if (!process.browser) {
    return;
  }

  if (!tabFocusListenersInitialized) {
    window.addEventListener("focus", handleTabFocus);
    window.addEventListener("pageshow", handleTabFocus);
    document.addEventListener("visibilitychange", handleTabFocus);
    tabFocusListenersInitialized = true;
  }

  if (tabConnectionChannel || !window.BroadcastChannel) {
    return;
  }

  tabConnectionChannel = new BroadcastChannel("chesshotel:live-connection");
  tabConnectionChannel.onmessage = ({ data }) => {
    if (
      data?.type !== "use-tab" ||
      data.tabId === tabId ||
      data.username !== userStore.getState().username
    ) {
      return;
    }
    if (hasActiveWebsocketConnection()) {
      otherUserLogin(true);
    } else if (wsConnectionStore.getState().otherLoginShow) {
      setOtherLoginSameBrowser(true);
    }
  };
}

function announceTabOwnership() {
  ensureTabConnectionChannel();
  tabConnectionChannel?.postMessage({
    type: "use-tab",
    tabId,
    username: userStore.getState().username,
  });
}

export function useConnectionInThisTab() {
  setOtherLoginShow(false);
  setOtherLoginSameBrowser(false);
  initWebsocketConnect();
}

export function handleWebsocketRejection(data: any) {
  if (!isTerminalWebsocketRejection(data)) {
    return;
  }

  terminalWebsocketRejection = String(data.reason || "connection_limited");
  clearInterval(pingInterval);
  setWsConnected(false);
  endReconnectTimer();
  setConnectionBlockedReason(terminalWebsocketRejection);
  wsDebug("websocket login rejected", {
    cmd: data.cmd,
    reason: terminalWebsocketRejection,
    retryAfterSeconds: data.retryAfterSeconds,
  });
}

export function setWebsocketWatchdogLiveGameActive(active: boolean) {
  watchdogLiveGameActive = active;
}

export function isLongPollingActive() {
  return useLongPolling;
}

function shouldUseLongPollingTransport() {
  if (!process.browser) {
    return false;
  }

  const params = new URLSearchParams(window.location.search || "");
  return (
    params.get("transport") === "polling" || params.get("forcePolling") === "1"
  );
}

function isRussiaLikelyClient() {
  if (!process.browser) {
    return false;
  }

  const { geo, country } = userStore.getState();
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname || "";
  const language = navigator.language || "";
  const languages = Array.from(navigator.languages || []);

  return (
    String(geo || "").toUpperCase() === "RU" ||
    String(country || "").toUpperCase() === "RU" ||
    host === "ru.chesshotel.com" ||
    host === "test-ru.chesshotel.com" ||
    /^\/ru(?:\/|$)/.test(path) ||
    /^ru\b/i.test(language) ||
    languages.some((item) => /^ru\b/i.test(item || "")) ||
    window.__chesshotelRussiaClientHealth?.enabled === true
  );
}

function redirectLikelyRussiaClientToEdge(
  reason: string,
  payload: Record<string, any> = {}
) {
  if (!isRussiaLikelyClient()) {
    return false;
  }

  return redirectRussiaToFallback(reason, payload, { edgeOnly: true });
}

function isNetworkLikeSiteSettingsFailure(error: any) {
  const message = String(error?.message || "");
  const code = String(error?.code || "");
  const status = error?.response?.status;

  return (
    status == null &&
    (code === "ECONNABORTED" ||
      /timeout/i.test(message) ||
      /network error/i.test(message))
  );
}

function isAutoLongPollingFallbackDisabled() {
  if (!process.browser) {
    return true;
  }

  const params = new URLSearchParams(window.location.search || "");
  if (params.get("disablePollingFallback") === "1") {
    return true;
  }

  try {
    return localStorage.getItem("chesshotel:disable-polling-fallback") === "1";
  } catch (_) {
    return false;
  }
}

function hasAutoLongPollingLiveGameContext(payload: Record<string, any> = {}) {
  if (watchdogLiveGameActive || payload.gameId) {
    return true;
  }

  if (!process.browser) {
    return false;
  }

  return AUTO_LONG_POLLING_GAME_PATH_RE.test(window.location.pathname || "");
}

function getAutoLongPollingEligibility(payload: Record<string, any> = {}) {
  const { longPollingUrl } = wsConnectionStore.getState();
  const { username } = userStore.getState();

  return {
    enabled: AUTO_LONG_POLLING_FALLBACK_ENABLED,
    disabled: isAutoLongPollingFallbackDisabled(),
    russiaLikely: isRussiaLikelyClient(),
    online: process.browser ? navigator.onLine : false,
    visible: process.browser ? document.visibilityState === "visible" : false,
    hasLongPollingUrl: !!longPollingUrl,
    hasUsername: !!username,
    liveGameContext: hasAutoLongPollingLiveGameContext(payload),
    alreadyStarted: autoLongPollingFallbackStarted || useLongPolling,
  };
}

function canStartAutoLongPollingFallback(payload: Record<string, any> = {}) {
  const eligibility = getAutoLongPollingEligibility(payload);
  return (
    eligibility.enabled &&
    !eligibility.disabled &&
    eligibility.russiaLikely &&
    eligibility.online &&
    eligibility.visible &&
    eligibility.hasLongPollingUrl &&
    eligibility.hasUsername &&
    eligibility.liveGameContext &&
    !eligibility.alreadyStarted
  );
}

async function startLongPollingTransport(
  reason: string,
  payload: Record<string, any> = {}
) {
  if (useLongPolling || longPollingSwitchStarted) {
    return;
  }

  longPollingSwitchStarted = true;
  useLongPolling = true;
  clearInterval(pingInterval);

  const { username } = userStore.getState();
  const sessionId = getOrCreateSessionId();
  const { longPollingUrl } = wsConnectionStore.getState();
  const { gamePlayStore } = await import("../redux/gamePlay");
  const { gameId } = gamePlayStore.getState();
  const { longPollingGetRequest } = await import("./longPolling");

  reportRussiaWebsocketEvent("long_polling_enabled", {
    reason,
    ...payload,
    url: longPollingUrl,
    websocketUrl: websocket?.url || null,
    gameId: gameId || null,
  });

  setWsConnected(true);
  endReconnectTimer();
  setupOfflineDetection();
  longPollingGetRequest(username, sessionId, gameId || "");
}

function startAutoLongPollingFallback(
  reason: string,
  payload: Record<string, any> = {}
) {
  const eligibility = getAutoLongPollingEligibility(payload);

  if (!canStartAutoLongPollingFallback(payload)) {
    reportRussiaWebsocketEvent("long_polling_auto_fallback_skipped", {
      reason,
      eligibility,
      ...payload,
    });
    return false;
  }

  autoLongPollingFallbackStarted = true;
  void startLongPollingTransport(reason, {
    automatic: true,
    eligibility,
    ...payload,
  }).catch((error) => {
    autoLongPollingFallbackStarted = false;
    useLongPolling = false;
    longPollingSwitchStarted = false;
    reportRussiaWebsocketEvent("long_polling_start_failed", {
      reason,
      message: error?.message,
    });
    initWebsocketConnect();
  });

  return true;
}

export function stopLongPollingTransport(
  reason: string,
  payload: Record<string, any> = {}
) {
  if (!useLongPolling && !longPollingSwitchStarted) {
    return;
  }

  useLongPolling = false;
  longPollingSwitchStarted = false;
  autoLongPollingFallbackStarted = false;

  reportRussiaWebsocketEvent("long_polling_stopped", {
    reason,
    ...payload,
  });

  setWsConnected(false);
  disconnectWebSocket(`stop long polling: ${reason}`);
  initWebsocketConnect();
}

function getWebsocketUserKey() {
  const { username, isGuest, id, room } = userStore.getState();
  return [username || "", isGuest ? "guest" : "member", id || "", room || ""]
    .map((value) => String(value))
    .join(":");
}

function getBrowserConnectionInfo() {
  if (!process.browser) {
    return {};
  }

  const nav = navigator as any;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection || {};

  return {
    networkState: navigator.onLine ? "online" : "offline",
    visibilityState: document.visibilityState,
    effectiveType: connection.effectiveType || null,
    downlink: connection.downlink || null,
    rtt: connection.rtt || null,
    saveData: connection.saveData || false,
  };
}

type WebsocketDiscoveryResult = {
  ws: number;
  url: string;
  responsive: boolean;
  players: number | null;
  games: number | null;
  instance: string | null;
  elapsedMs: number;
  error?: string;
};

function normalizeWsNumber(ws: any) {
  const numeric = Number(ws);
  return WEBSOCKET_DISCOVERY_WS_NUMBERS.includes(numeric) ? numeric : null;
}

function getFiniteNumber(value: any) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function buildWebsocketUrl(wsNumber: number) {
  const serverInstance = `ws${wsNumber}`;
  const host = USE_PROD ? getServiceHost() : getConfiguredHost();
  const testPort = WEBSOCKET_DISCOVERY_TEST_PORTS[wsNumber];

  return USE_PROD
    ? `wss://${host}/${serverInstance}/`
    : `ws://${host}:${testPort}`;
}

function getLastKnownWs() {
  if (!process.browser) {
    return null;
  }

  try {
    return normalizeWsNumber(localStorage.getItem(LAST_KNOWN_WS_STORAGE_KEY));
  } catch (_) {
    return null;
  }
}

function setLastKnownWs(ws: any) {
  const normalized = normalizeWsNumber(ws);
  if (!process.browser || !normalized) {
    return;
  }

  try {
    localStorage.setItem(LAST_KNOWN_WS_STORAGE_KEY, String(normalized));
  } catch (_) {}
}

function hasActiveWebsocketConnection() {
  return (
    !!websocket &&
    (websocket.readyState === 0 || websocket.readyState === 1)
  );
}

function probeWebsocketServer(
  wsNumber: number
): Promise<WebsocketDiscoveryResult> {
  const startedAt = Date.now();
  const url = buildWebsocketUrl(wsNumber);

  return new Promise((resolve) => {
    if (!process.browser || !window.WebSocket) {
      resolve({
        ws: wsNumber,
        url,
        responsive: false,
        players: null,
        games: null,
        instance: null,
        elapsedMs: Date.now() - startedAt,
        error: "websocket_unavailable",
      });
      return;
    }

    let socket: WebSocket | null = null;
    let finished = false;
    let responsive = false;
    let timeout: any;

    const finish = (result: Partial<WebsocketDiscoveryResult>) => {
      if (finished) {
        return;
      }

      finished = true;
      clearTimeout(timeout);

      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;

        try {
          if (socket.readyState === 0 || socket.readyState === 1) {
            socket.close(1000, "websocket discovery complete");
          }
        } catch (_) {}
      }

      resolve({
        ws: wsNumber,
        url,
        responsive,
        players: null,
        games: null,
        instance: null,
        elapsedMs: Date.now() - startedAt,
        ...result,
      });
    };

    timeout = setTimeout(() => {
      finish({
        responsive,
        error: responsive ? "stat_timeout" : "timeout",
      });
    }, WEBSOCKET_DISCOVERY_TIMEOUT_MS);

    try {
      socket = new window.WebSocket(url);
    } catch (error) {
      finish({
        responsive: false,
        error: error?.message || "create_failed",
      });
      return;
    }

    socket.onopen = () => {
      responsive = true;

      try {
        socket?.send(JSON.stringify({ cmd: "stat" }));
        socket?.send("ping");
      } catch (error) {
        finish({
          responsive: false,
          error: error?.message || "send_failed",
        });
      }
    };

    socket.onmessage = (event) => {
      if (event.data === "pong") {
        responsive = true;
        return;
      }

      try {
        const data = JSON.parse(event.data);
        if (data?.cmd === "STAT") {
          finish({
            responsive: true,
            players: getFiniteNumber(data.players),
            games: getFiniteNumber(data.games),
            instance: data.instance == null ? null : String(data.instance),
          });
        }
      } catch (_) {}
    };

    socket.onerror = () => {
      if (!responsive) {
        finish({
          responsive: false,
          error: "websocket_error",
        });
      }
    };

    socket.onclose = () => {
      if (!finished && !responsive) {
        finish({
          responsive: false,
          error: "closed_before_response",
        });
      }
    };
  });
}

function getPreferredWsOrder() {
  const order = [
    getLastKnownWs(),
    normalizeWsNumber(wsConnectionStore.getState().ws),
    2,
    1,
    3,
  ];
  const seen = new Set<number>();

  return order.filter((ws): ws is number => {
    if (!ws || seen.has(ws)) {
      return false;
    }

    seen.add(ws);
    return true;
  });
}

function getPreferenceIndex(ws: number, preferredWs: number[]) {
  const index = preferredWs.indexOf(ws);
  return index === -1 ? preferredWs.length : index;
}

function selectWebsocketDiscoveryResult(
  results: WebsocketDiscoveryResult[]
) {
  const responsive = results.filter((result) => result.responsive);
  if (!responsive.length) {
    return null;
  }

  const withPlayers = responsive.filter((result) => result.players != null);
  const candidates = withPlayers.length ? withPlayers : responsive;
  const preferredWs = getPreferredWsOrder();

  return candidates.slice().sort((a, b) => {
    if (withPlayers.length) {
      const playerDiff = (b.players || 0) - (a.players || 0);
      if (playerDiff) {
        return playerDiff;
      }
    }

    const preferredDiff =
      getPreferenceIndex(a.ws, preferredWs) -
      getPreferenceIndex(b.ws, preferredWs);
    if (preferredDiff) {
      return preferredDiff;
    }

    const latencyDiff = a.elapsedMs - b.elapsedMs;
    if (latencyDiff) {
      return latencyDiff;
    }

    return a.ws - b.ws;
  })[0];
}

async function discoverWebsocketFromServers(
  reason: string,
  payload: Record<string, any> = {}
) {
  if (!process.browser || useLongPolling) {
    return null;
  }

  if (websocketDiscoveryPromise) {
    return websocketDiscoveryPromise;
  }

  websocketDiscoveryPromise = (async () => {
    if (hasActiveWebsocketConnection()) {
      return normalizeWsNumber(wsConnectionStore.getState().ws);
    }

    reportRussiaWebsocketEvent("websocket_discovery_start", {
      reason,
      ...payload,
    });

    const results = await Promise.all(
      WEBSOCKET_DISCOVERY_WS_NUMBERS.map((wsNumber) =>
        probeWebsocketServer(wsNumber)
      )
    );
    const selected = selectWebsocketDiscoveryResult(results);

    reportRussiaWebsocketEvent("websocket_discovery_result", {
      reason,
      selectedWs: selected?.ws || null,
      selectedPlayers: selected?.players ?? null,
      results,
      ...payload,
    });

    if (!selected) {
      redirectLikelyRussiaClientToEdge("websocket_discovery_null", {
        discoveryReason: reason,
        ...payload,
      });
      return null;
    }

    setLastKnownWs(selected.ws);
    setWebsocketUrl(selected.ws);
    connectToWebSocket();

    return selected.ws;
  })().finally(() => {
    websocketDiscoveryPromise = null;
  });

  return websocketDiscoveryPromise;
}

function setPingErrorData(update: {
  eventSequence?: Record<string, any>;
  websocketWatchdog?: Record<string, any>;
  [key: string]: any;
}) {
  if (process.env.NODE_ENV === "development") {
    return;
  }

  import("../redux/ping").then((module) => {
    const state = module.pingStore.getState();
    module.setErrorData({
      ...update,
      ...(update.eventSequence && {
        eventSequence: {
          ...state.errorData.eventSequence,
          ...update.eventSequence,
        },
      }),
      ...(update.websocketWatchdog && {
        websocketWatchdog: {
          ...state.errorData.websocketWatchdog,
          ...update.websocketWatchdog,
        },
      }),
    });
  });
}

function getWatchdogState(reason: string, now = Date.now()) {
  return {
    reason,
    lastCheckAt: now,
    lastPingAt: lastPingAt || null,
    lastPongAt: lastPongAt || null,
    lastMessageAt: lastMessageAt || null,
    websocketOpenedAt: websocketOpenedAt || null,
    stalePongMs: lastPongAt ? now - lastPongAt : null,
    staleMessageMs: lastMessageAt ? now - lastMessageAt : null,
    openedForMs: websocketOpenedAt ? now - websocketOpenedAt : null,
    readyState: websocket?.readyState ?? null,
    url: websocket?.url || null,
    liveGameActive: watchdogLiveGameActive,
    reconnectWatchdogEnabled: false,
    ...getBrowserConnectionInfo(),
  };
}

function recordWatchdogState(reason: string, now = Date.now()) {
  setPingErrorData({
    websocketWatchdog: getWatchdogState(reason, now),
  });
}

function getCompactWatchdogState(state?: Record<string, any> | null) {
  if (!state) {
    return null;
  }

  return {
    reason: state.reason,
    lastCheckAt: state.lastCheckAt,
    stalePongMs: state.stalePongMs,
    staleMessageMs: state.staleMessageMs,
    openedForMs: state.openedForMs,
    readyState: state.readyState,
    reconnectWatchdogEnabled: state.reconnectWatchdogEnabled,
    networkState: state.networkState,
    visibilityState: state.visibilityState,
    effectiveType: state.effectiveType,
    rtt: state.rtt,
  };
}

function setupOfflineDetection() {
  if (offlineDetectionInitialized) return;
  offlineDetectionInitialized = true;

  window.addEventListener("offline", (event) => {
    import("../redux/ping").then((module) => {
      module.setOffline();
      module.setErrorData({
        networkState: "offline",
        networkStateTime: Date.now(),
      });

      offlineTimeOut = setTimeout(() => {
        startReconnectTimer();
      }, 3000);
    });
  });

  window.addEventListener("online", (event) => {
    import("../redux/ping").then((module) => {
      module.setErrorData({
        networkState: "online",
        networkStateTime: Date.now(),
      });
      module.resetRetries();
      clearTimeout(offlineTimeOut);
      endReconnectTimer();
      initWebsocketConnect();
    });
  });
}

export function getOrCreateSessionId() {
  let sessionId: string = sessionStorage.getItem("sessionId");
  if (!sessionId) {
    sessionId = v4();
    sessionStorage.setItem("sessionId", sessionId);
  }
  return sessionId;
}

async function loginServer() {
  const { username, isGuest, id, room } = userStore.getState();
  import("../redux/gamePlay").then(({ gamePlayStore }) => {
    const { gameId } = gamePlayStore.getState();

    try {
      const sessionId = getOrCreateSessionId();

      const user = {
        username,
        isGuest,
        userId: id,
        room,
        gameId,
        sessionId,
      };

      LoginWs.loginServer(user);
      reportRussiaWebsocketEvent("websocket_login_sent", {
        isGuest,
        userId: id || null,
        room,
        gameId: gameId || null,
      });
    } catch (e) {
      setTimeout(() => {
        loginServer();
      }, 3000);
    }
  });
}

function startReconnectTimer() {
  const reconnectStart = wsConnectionStore.getState().reconnectStart;
  if (!reconnectStart) {
    doStartReconnectTimer();

    const loop = () => {
      const startTime = wsConnectionStore.getState().reconnectStart;
      if (startTime == null) {
        return;
      }
      updateReconnectTimer();
      requestAnimationFrame(loop);
    };

    loop();
  }
}

export function initWebsocketConnect() {
  if (
    !process.browser ||
    isReadingRoute(window.location.pathname) ||
    wsConnectionStore.getState().otherLoginShow
  ) {
    return;
  }

  ensureTabConnectionChannel();

  // A tab opened in the background waits until it is selected to claim the
  // live connection from another tab in the same browser.
  if (document.visibilityState === "hidden") {
    return;
  }

  if (terminalWebsocketRejection) {
    if (userStore.getState().isGuest) {
      return;
    }

    terminalWebsocketRejection = null;
    setConnectionBlockedReason(null);
  }

  if (!shouldUseLongPollingTransport() && !autoLongPollingFallbackStarted) {
    useLongPolling = false;
    longPollingSwitchStarted = false;
  }

  if (useLongPolling) {
    return;
  }

  wsDebug("initWebsocketConnect: fetching site settings", {
    baseURL: api.defaults.baseURL,
  });

  api
    .get(`/site/settings?_=${new Date().getTime()}`, {
      timeout: SITE_SETTINGS_TIMEOUT_MS,
    })
    .then(async (response) => {
      if (
        isReadingRoute(window.location.pathname) ||
        wsConnectionStore.getState().otherLoginShow
      ) {
        return;
      }
      const { ws, geo } = response.data;
      wsDebug("site/settings response", {
        ws,
        geo,
        host: process.env.NEXT_PUBLIC_HOST,
      });
      reportRussiaSiteSettings({ geo, ws });

      setLastKnownWs(ws);
      setWebsocketUrl(ws);
      connectToWebSocket();
      const localGeo = localStorage.getItem("geo");
      if (localGeo) {
        setGeo(localGeo);
      } else {
        setGeo(geo);
      }
    })
    .catch((error) => {
      console.log("get ws number error", error);
      wsDebug("site/settings request failed", {
        message: error?.message,
        status: error?.response?.status,
      });
      reportRussiaSiteSettingsFailed(error);
      const payload = {
        message: error?.message,
        status: error?.response?.status,
        code: error?.code,
      };
      if (
        isNetworkLikeSiteSettingsFailure(error) &&
        redirectLikelyRussiaClientToEdge("site_settings_failed", payload)
      ) {
        return;
      }

      const discovery = hasActiveWebsocketConnection()
        ? Promise.resolve(normalizeWsNumber(wsConnectionStore.getState().ws))
        : discoverWebsocketFromServers("site_settings_failed", payload);

      discovery
        .catch((discoveryError) => {
          reportRussiaWebsocketEvent("websocket_discovery_failed", {
            reason: "site_settings_failed",
            message: discoveryError?.message,
            ...payload,
          });
        })
        .finally(() => {
          setTimeout(
            () => {
              initWebsocketConnect();
            },
            hasActiveWebsocketConnection()
              ? SITE_SETTINGS_BACKGROUND_RETRY_MS
              : SITE_SETTINGS_RETRY_MS
          );
        });
    });
}

//Happens when server code update happens and it switches to a new server instance
export async function checkWs(ws: number) {
  setLastKnownWs(ws);

  import("../redux/gamePlay").then(({ gamePlayStore }) => {
    const { gameEnded } = gamePlayStore.getState();

    const isPlaying = !gameEnded;
    if (isPlaying) {
      const unsubscribe = gamePlayStore.subscribe(({ gameEnded }) => {
        if (gameEnded && wsConnectionStore.getState().ws !== ws) {
          setTimeout(() => {
            unsubscribe();
            setWebsocketUrl(ws);
            connectToWebSocket();
          }, 2000);
        }
      });
    }

    if (!isPlaying && wsConnectionStore.getState().ws !== ws) {
      setWebsocketUrl(ws);
      connectToWebSocket();
    }
  });
}

export function otherUserLogin(sameBrowser = false) {
  const connection = wsConnectionStore.getState();
  setOtherLoginSameBrowser(
    sameBrowser ||
      (connection.otherLoginShow && connection.otherLoginSameBrowser),
  );
  disconnectWebSocket("other login");
  setWsConnected(false);
  endReconnectTimer();
  setOtherLoginShow(true);
}

export function disconnectWebSocket(reason = "client reconnect") {
  clearInterval(pingInterval);

  if (websocket) {
    websocket.onclose = function () {};
    try {
      websocket.close(CLIENT_RECONNECT_CLOSE_CODE, reason);
    } catch (e) {
      websocket.close();
    }
  }
}

let retries = 0;
const LIVE_CLOSE_REPORT_DEDUPE_MS = 30000;
let lastLiveCloseReportKey = "";
let lastLiveCloseReportAt = 0;

export function incrRetries() {
  retries++;
}

function shouldReportLiveClose(
  gameId: string | null,
  code: number,
  now: number,
) {
  const key = `${gameId || "no-game"}:${code}`;
  if (
    lastLiveCloseReportKey === key &&
    now - lastLiveCloseReportAt < LIVE_CLOSE_REPORT_DEDUPE_MS
  ) {
    return false;
  }

  lastLiveCloseReportKey = key;
  lastLiveCloseReportAt = now;
  return true;
}

function noteLiveGame1006Close(gameId: string, now: number) {
  if (
    liveGame1006CloseGameId !== gameId ||
    !firstLiveGame1006CloseAt ||
    now - firstLiveGame1006CloseAt > AUTO_LONG_POLLING_CLOSE_WINDOW_MS
  ) {
    liveGame1006CloseGameId = gameId;
    liveGame1006CloseCount = 0;
    firstLiveGame1006CloseAt = now;
  }

  liveGame1006CloseCount += 1;
  return liveGame1006CloseCount;
}

function connectToWebSocket() {
  if (
    isReadingRoute(window.location.pathname) ||
    wsConnectionStore.getState().otherLoginShow
  ) {
    return;
  }

  const url = wsConnectionStore.getState().websocketUrl;
  const userKey = getWebsocketUserKey();

  if (
    websocket &&
    websocket.url === url &&
    websocketUserKey === userKey &&
    (websocket.readyState === WebSocket.OPEN ||
      websocket.readyState === WebSocket.CONNECTING)
  ) {
    wsDebug("connectToWebSocket: reusing existing socket", {
      url,
      readyState: websocket.readyState,
    });
    return;
  }

  disconnectWebSocket("client reconnect");

  setWsConnected(false);
  websocketOpenedAt = 0;
  lastPingAt = 0;
  lastPongAt = 0;
  lastMessageAt = 0;
  websocketUserKey = userKey;

  wsDebug("connectToWebSocket", { url });
  websocket = new window.WebSocket(url);

  websocket.onerror = (error: Event) => {
    wsDebug("websocket error", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_error", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });

    import("../redux/ping").then((module) => {
      module.setWsError();
      module.setErrorData({
        lastWebsocketError: {
          readyState: websocket?.readyState,
          url: websocket?.url,
          timestamp: Date.now(),
        },
      });
    });
  };

  websocket.onclose = (e: CloseEvent) => {
    if (useLongPolling) {
      reportRussiaWebsocketEvent("websocket_close_ignored_for_polling", {
        code: e.code,
        reason: e.reason,
        wasClean: e.wasClean,
        url: websocket?.url,
      });
      return;
    }

    console.log("websocket close");
    wsDebug("websocket close", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_close", {
      code: e.code,
      reason: e.reason,
      wasClean: e.wasClean,
      readyState: websocket?.readyState,
      url: websocket?.url,
      retries,
    });

    if (terminalWebsocketRejection) {
      wsDebug("websocket reconnect suppressed after server rejection", {
        reason: terminalWebsocketRejection,
        code: e.code,
      });
      return;
    }

    import("../redux/ping").then((module) => {
      import("../redux/gamePlay").then(({ gamePlayStore }) => {
        const now = Date.now();
        const pingState = module.pingStore.getState();
        const closeReadyState = websocket?.readyState ?? null;
        const closeUrl = websocket?.url || null;
        module.setWsClose();
        module.setWsCloseErrorMsg(e.code + " " + e.reason);
        module.setDisconnectTime();
        module.setErrorData({
          eventSequence: {
            ...pingState.errorData.eventSequence,
            lastWsClose: now,
            timeSinceLastVisibilityChange:
              now -
              (pingState.errorData.connectionState.lastVisibilityChange || 0),
            timeSinceLastPing:
              now - (pingState.errorData.eventSequence.lastPingSent || 0),
            timeSinceLastPong:
              now - (pingState.errorData.eventSequence.lastPongReceived || 0),
          },
          networkStateOnClose:
            process.browser && navigator.onLine ? "online" : "offline",
          networkStateTimeOnClose: now,
          websocketWatchdog: {
            ...pingState.errorData.websocketWatchdog,
            ...getWatchdogState("websocket-close", now),
          },
        });

        const { gameEnded, gameId } = gamePlayStore.getState();
        const isPlaying = gameEnded == false;
        module.setErrorData({
          wsCloseIsPlaying: isPlaying,
          wsCloseTime: now,
          wsCloseGameId: gameId,
        });

        if (isPlaying && gameId && shouldReportLiveClose(gameId, e.code, now)) {
          module.sendWebsocketCloseError({
            code: e.code,
            reason: e.reason,
            wasClean: e.wasClean,
            readyState: closeReadyState,
            url: closeUrl,
            gameId,
            isPlaying,
            timestamp: now,
            sessionId: getOrCreateSessionId(),
          });
        }

        if (isPlaying && gameId && e.code === 1006) {
          const closeCount = noteLiveGame1006Close(gameId, now);
          if (
            closeCount >= AUTO_LONG_POLLING_MIN_1006_CLOSES &&
            startAutoLongPollingFallback("repeated_1006_live_game_close", {
              gameId,
              closeCount,
              firstCloseAt: firstLiveGame1006CloseAt,
              closeWindowMs: now - firstLiveGame1006CloseAt,
              closeEvent: {
                code: e.code,
                reason: e.reason,
                wasClean: e.wasClean,
                readyState: closeReadyState,
                url: closeUrl,
              },
              ping: {
                retries: pingState.retries,
                timeSinceLastPong:
                  now -
                  (pingState.errorData.eventSequence.lastPongReceived || 0),
                timeSinceLastVisibilityChange:
                  now -
                  (pingState.errorData.connectionState.lastVisibilityChange ||
                    0),
              },
              watchdog: getCompactWatchdogState(
                getWatchdogState("websocket-close", now)
              ),
            })
          ) {
            return;
          }
        }

        setTimeout(() => {
          module.setRetries();
          retries++;
          startReconnectTimer();
          initWebsocketConnect();
        }, 1000);
      });
    });
  };
  websocket.onopen = () => {
    console.log("websocket open");
    wsDebug("websocket open", {
      readyState: websocket?.readyState,
      url: websocket?.url,
    });
    reportRussiaWebsocketEvent("websocket_open", {
      readyState: websocket?.readyState,
      url: websocket?.url,
      retries,
    });

    setWsConnected(true);

    endReconnectTimer();
    websocketOpenedAt = Date.now();
    lastPongAt = websocketOpenedAt;
    lastMessageAt = websocketOpenedAt;
    recordWatchdogState("websocket-open", websocketOpenedAt);
    startPing();
    websocket.sendPing();

    import("../redux/lobby").then((module) => {
      module.clearInvites();
      module.enterLobby();
    });

    loginServer();
    setupOfflineDetection();
  };

  websocket.onmessage = async (e) => {
    const messageAt = Date.now();
    lastMessageAt = messageAt;

    if (e.data === "pong") {
      lastPongAt = messageAt;
      reportRussiaWebsocketEvent("websocket_pong", {
        latencyMs: lastPingAt ? lastPongAt - lastPingAt : null,
        url: websocket?.url,
      });
      setPingErrorData({
        eventSequence: {
          lastPongReceived: lastPongAt,
        },
        websocketWatchdog: getWatchdogState("pong", lastPongAt),
      });
      clearTimeout(postTimeout);
      return;
    }

    let data: any;
    try {
      data = JSON.parse(e.data);
    } catch (e) {
      console.log("JSON parse error", e);
      wsDebug("websocket JSON parse error", {
        raw: typeof e?.toString === "function" ? e.toString() : "unknown",
      });
      return;
    }
    if (data?.cmd === "COMP" || data?.cmd === "ONLI" || data?.cmd === "WS") {
      wsDebug("websocket message", data);
    }
    if (
      data?.cmd === "COMP" &&
      document.visibilityState !== "hidden" &&
      !wsConnectionStore.getState().otherLoginShow
    ) {
      announceTabOwnership();
    }
    routeIncommingMessage(data);

    if (data?.cmd === "COMP" && shouldUseLongPollingTransport()) {
      void startLongPollingTransport("manual_param_login_complete", {
        automatic: false,
      });
    }
  };

  websocket.sendPing = () => {
    try {
      lastPingAt = Date.now();
      websocket.send("ping");
      setPingErrorData({
        eventSequence: {
          lastPingSent: lastPingAt,
        },
        websocketWatchdog: getWatchdogState("ping", lastPingAt),
      });
    } catch (e) {
      setTimeout(() => {
        websocket.sendPing();
      }, 1000);
    }
  };
}

function startPing() {
  clearInterval(pingInterval);

  pingInterval = setInterval(() => {
    try {
      if (websocket && websocket.readyState == WebSocket.OPEN) {
        websocket.sendPing();
      }
    } catch (e) {
      console.log(e);
    }
  }, PING_INTERVAL_MS);
}

// async function longPollingConnect() {
//   console.log("switchToLongPolling");
//   disconnectWebSocket();

//   useLongPolling = true;
//   const username = userStore.getState().username;
//   const sessionId = sessionStorage.getItem("sessionId");

//   const { gamePlayStore } = await import("../redux/gamePlay");
//   const { gameId } = gamePlayStore.getState();

//   await longPollingGetRequest(username, sessionId, gameId);
//   setWsConnected(true);
// }
